import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { unitId as brandUnitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { Field } from '@/components/Field'
import { FIELD_CONTROL_CLASSNAME } from '@/styles/formControls'
import { Icon } from '@/components/Icon'
import { Note } from '@/components/Note'
import { MRpoint } from '@/screens/m-rpoint/MRpoint'
import { WizardProse, WizardButtons, WizardSplit, WizardCard, WizardHeading } from '@/components/WizardParts'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { formatJobTime, TRIGGER_LABEL } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'
import type { RestoreWizardConfig, RestorePoint } from '@/layers/restore-wizard/types'

type WorkspaceSummary = components['schemas']['WorkspaceSummary']

/**
 * `repo-wz-1`/`access-wz-1`/`project-wz-1`/`ci-wz-1`/`settings-wz-1`/`ref-wz-1`/`review-wz-1`/
 * `wshook-wz-1` — the `select` role's shared pane (`_family-wizard.md`). "**Which restore point,
 * and from when?**" — repositories/projects (or, for `ref`, the single repository) are picked on
 * the next pane now, never here.
 *
 * **Rebuilt 2026-09-16 to match `Prototype_v2.html`'s own restore-point field for real, at the
 * user's direct request.** The prototype's "Restore point" field is not a display, and the table
 * of snapshots is not inlined on this pane at all — it is a clickable control (icon, the chosen
 * point's own summary, a chevron) that opens `m-rpoint`, a modal carrying the real picker table.
 * The previous build got this backwards: a read-only `Chip` here, and the entire snapshot table
 * inlined as its own panel below — which is also where "Include expired restore points" and a
 * "Recovering" field (a second field this pane never had in the prototype at all) had come from.
 * `MRpoint` was already fully built (`m-rpoint`, this session, for `m-rpoint`'s own screen) and
 * simply never wired to anything — reused here rather than rebuilt.
 */
export function SelectRestorePointPane({
  config,
  heading,
  onNext,
  onCancel,
}: {
  config: RestoreWizardConfig
  heading: string
  onNext: (args: { restorePoint: RestorePoint }) => void
  onCancel: () => void
}) {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const navigate = useNavigate()

  const sourceWorkspace = useQuery({
    queryKey: ['workspace-name', orgId, workspaceId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — this is a
    // restore wizard's first pane, directly reachable on mount, so the race is not theoretical here.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<WorkspaceSummary | undefined> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items as unknown as WorkspaceSummary[]).find((w) => w.workspace_id === workspaceId)
    },
  })

  const [restorePoint, setRestorePoint] = useState<RestorePoint | undefined>()
  const [pickerOpen, setPickerOpen] = useState(false)

  const problem = sourceWorkspace.error instanceof ApiProblemError ? sourceWorkspace.error.problem : undefined

  return (
    <div data-scr={brandUnitId(config.unitIds.select)} data-cmp="pane">
      <WizardSplit>
        <div>
          <WizardHeading>{heading}</WizardHeading>
          <WizardProse>
            Every picker here reads the backup snapshot itself — never Bitbucket's current state — because a restore
            undoes something that already happened, and what happened is fixed the moment the snapshot was taken.
          </WizardProse>
        </div>

        <WizardCard>
          <h4 className="m-0 mb-4 text-body-lg font-semibold">Source</h4>

          <Field label="Source workspace">
            <div data-cmp="field-control" className={FIELD_CONTROL_CLASSNAME}>
              <span className="font-mono">{sourceWorkspace.data?.name ?? workspaceId}</span>
              {sourceWorkspace.data?.region && <Chip variant="plain">{sourceWorkspace.data.region}</Chip>}
            </div>
          </Field>

          <Field label="Restore point">
            <button
              type="button"
              data-cmp="field-control"
              className={`${FIELD_CONTROL_CLASSNAME} w-full cursor-pointer text-left`}
              onClick={() => setPickerOpen(true)}
            >
              {restorePoint ? (
                <>
                  <Icon name="i-verify" size="s" color="ok" />
                  <span className="font-mono">
                    {restorePoint.data_snapshot.handle} · {restorePoint.started_at ? restorePoint.started_at.display : formatJobTime(restorePoint.data_snapshot.started_at)}
                  </span>
                  <Chip variant="plain">{TRIGGER_LABEL[restorePoint.data_snapshot.trigger] ?? restorePoint.data_snapshot.trigger}</Chip>
                </>
              ) : (
                <span className="text-muted">Not yet chosen</span>
              )}
              <Icon name="i-chevD" size="s" color="muted" className="ml-auto" />
            </button>
            {restorePoint?.configuration_snapshot && (
              <Note>
                Paired configuration snapshot{' '}
                <b>
                  {restorePoint.configuration_snapshot.handle} · {formatJobTime(restorePoint.configuration_snapshot.started_at)}
                </b>{' '}
                ·{' '}
                {TRIGGER_LABEL[restorePoint.configuration_snapshot.trigger] ?? restorePoint.configuration_snapshot.trigger} — the newest at or before this
                restore point.
              </Note>
            )}
            {restorePoint && (
              <Note>
                <button type="button" data-cmp="link" className="underline text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => setPickerOpen(true)}>
                  Choose a different restore point
                </button>{' '}
                ·{' '}
                <button type="button" data-cmp="link" className="underline text-link bg-transparent border-0 p-0 cursor-pointer" onClick={() => navigate('/snapshots')}>
                  see all restore points
                </button>
              </Note>
            )}
          </Field>
        </WizardCard>
      </WizardSplit>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <WizardButtons>
        <Button data-act={undefined} ghost onClick={onCancel}>
          Cancel
        </Button>
        <Button data-act={actionId(`nav.${config.unitIds.repositories ?? config.unitIds.targets}`)} disabled={!restorePoint} onClick={() => restorePoint && onNext({ restorePoint })}>
          Next
        </Button>
      </WizardButtons>

      {pickerOpen && (
        <MRpoint
          orgId={orgId}
          workspaceId={workspaceId}
          selectedJobId={restorePoint?.data_snapshot.job_id}
          onDismiss={() => setPickerOpen(false)}
          onSelect={(point) => {
            setRestorePoint(point)
            setPickerOpen(false)
          }}
          onSeeAll={() => navigate('/snapshots')}
        />
      )}
    </div>
  )
}
